import { useState } from "react";
import { FONT_BODY, FONT_DISPLAY } from "./theme";

// ============================================================
// BLACK OWL - Legal Panel (Terms of Service & Privacy Policy)
// ============================================================
// HOW TO UPDATE THE LEGAL TEXT:
// The full text of each document lives in the two constants
// below (TERMS_SECTIONS and PRIVACY_SECTIONS). Each is an array
// of { heading, body } objects. Edit those to update content.
//
// PLACEHOLDERS TO REPLACE before publishing (find & replace):
//   [[COMPANY_NAME]]   -> your legal/business name
//   [[CONTACT_EMAIL]]  -> your support/contact email
//   [[MAILING_ADDRESS]]-> your mailing address
//   [[WEBSITE_URL]]    -> your final domain (from Porkbun)
//   [[LAST_UPDATED]]   -> the date you publish
// ============================================================

const TERMS_SECTIONS = [
  {
    heading: "Agreement to Terms",
    body: `These Terms of Service constitute a legally binding agreement made between you and [[COMPANY_NAME]] ("we," "us," or "our"), concerning your access to and use of the Black Owl application and website at [[WEBSITE_URL]] (the "Service"). By creating an account or using the Service, you agree that you have read, understood, and agree to be bound by these Terms. If you do not agree, you may not use the Service.`,
  },
  {
    heading: "Eligibility (18+)",
    body: `You must be at least 18 years of age to use the Service. By using the Service, you represent and warrant that you are at least 18 years old. The Service involves social challenges and penalties that may not be appropriate for minors.`,
  },
  {
    heading: "Assumption of Risk — Penalties & Challenges",
    body: `Black Owl is a social game in which players may assign real-life penalties, dares, or physical challenges (such as push-ups or other activities) to one another. All such penalties and challenges are created, agreed upon, and performed entirely by users at their own discretion and risk. [[COMPANY_NAME]] does not create, endorse, monitor, or control the specific penalties chosen by users. You acknowledge and agree that you participate in any real-life challenge voluntarily and at your own risk, and that [[COMPANY_NAME]] is not liable for any injury, harm, loss, or damage arising from any penalty, dare, or challenge performed in connection with the Service. You are solely responsible for ensuring any activity you undertake is safe, legal, and appropriate for you.`,
  },
  {
    // FB-28 draft. Needs review by a lawyer or Termly; not legal advice.
    heading: "Host-Created Penalties and Dares",
    body: `Black Owl lets a lobby host write custom penalties ("House Rules"). These are created by the host, not by Black Owl, and Black Owl does not review or endorse them. Participation is voluntary: you may decline any penalty at any time, and no one should be pressured, coerced, or embarrassed into performing one. Do not create or perform penalties that are dangerous, illegal, harassing, discriminatory, sexually explicit, or that involve alcohol or other substances, or that could cause injury. Use your own judgment about your abilities and surroundings. You are solely responsible for any penalty you choose to perform, and Black Owl is not liable for any injury, loss, or damage arising from a penalty.`,
  },
  {
    heading: "User Conduct",
    body: `You agree not to use the Service to harass, abuse, threaten, or harm others; to create offensive, obscene, or discriminatory team names or content; to cheat or manipulate scoring; to attempt to access other users' accounts; or to interfere with the proper functioning of the Service. We reserve the right to suspend or terminate accounts that violate these rules.`,
  },
  {
    heading: "Accounts",
    body: `You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate information and to keep it updated. Notify us immediately of any unauthorized use of your account.`,
  },
  {
    heading: "No Affiliation or Endorsement",
    body: `Black Owl is an independent product and is not affiliated with, endorsed by, sponsored by, or otherwise associated with the National Football League (NFL), NFL Properties LLC, any NFL member club, the NFL Players Association, ESPN, or any of their respective affiliates, subsidiaries, or licensors (collectively, the "Third-Party Entities"). All team names, player names, logos, and related marks are the property of their respective owners and are used solely for identification and informational purposes. Player statistics and game data are obtained from publicly available third-party sources, including ESPN. The Third-Party Entities have not reviewed, approved, or endorsed Black Owl, and are not responsible for the accuracy of any statistics, scoring, or other data displayed within the Service.`,
  },
  {
    heading: "Disclaimer of Warranties",
    body: `The Service is provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied. We do not warrant that the Service will be uninterrupted, secure, or error-free, or that any scoring or statistical data will be accurate or complete.`,
  },
  {
    heading: "Limitation of Liability",
    body: `To the fullest extent permitted by law, [[COMPANY_NAME]] shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or data, arising from your use of the Service or from any penalties or challenges undertaken in connection with the Service.`,
  },
  {
    heading: "Dispute Resolution",
    body: `Most concerns can be resolved quickly. Before filing any formal claim, you agree to first contact us at [[CONTACT_EMAIL]] and attempt to resolve the dispute informally for a period of at least sixty (60) days. If the dispute cannot be resolved informally, it shall be resolved through binding individual arbitration, and not in a court of law. You and [[COMPANY_NAME]] waive any right to a jury trial and agree not to participate in a class action.`,
  },
  {
    heading: "Governing Law",
    body: `These Terms are governed by the laws of the State of West Virginia, without regard to its conflict of law principles.`,
  },
  {
    heading: "Changes to These Terms",
    body: `We may update these Terms from time to time. Continued use of the Service after changes become effective constitutes acceptance of the revised Terms.`,
  },
  {
    heading: "Contact Us",
    body: `Questions about these Terms may be sent to [[CONTACT_EMAIL]] or [[MAILING_ADDRESS]].`,
  },
];

const COMMUNITY_SECTIONS = [
  {
    heading: "Community Guidelines",
    body: `Black Owl is meant to be fun, social, and safe. When you film and share penalty moments, keep it good-natured: penalties should be agreed on by everyone involved, safe to perform, and legal. Don't post content that features harassment, hate speech, nudity, dangerous stunts, hazing, content involving anyone under 18, or anything that pressures a person into something they haven't willingly agreed to. Content shared publicly by players is created by users, not by [[COMPANY_NAME]] — we don't produce, endorse, or monitor it, and views expressed in user content are the creators' own. We reserve the right to disassociate from, report, or request removal of any content that violates these guidelines or that misuses our name, handle, or brand. If you see something that crosses the line, report it to [[CONTACT_EMAIL]].`,
  },
  {
    // FB-28: same line as the House Rules card
    heading: "House Rules",
    body: `Penalties are voluntary. Keep them safe, legal, and fun.`,
  },
];

const PRIVACY_SECTIONS = [
  {
    heading: "Introduction",
    body: `This Privacy Policy explains how [[COMPANY_NAME]] ("we," "us," or "our") collects, uses, and protects your information when you use the Black Owl application and website at [[WEBSITE_URL]] (the "Service"). By using the Service, you agree to the practices described in this policy.`,
  },
  {
    heading: "Information We Collect",
    body: `We collect information you provide directly, including your email address, username, team name, and password (which is stored only in a securely hashed form — we never store your plain-text password). We also collect gameplay data such as your drafts, points, and penalties, and limited technical information such as your browser type and device information when you submit feedback.`,
  },
  {
    heading: "How We Use Your Information",
    body: `We use your information to operate and provide the Service, authenticate your account, run game sessions, respond to feedback and support requests, notify you about the Service (including waitlist and launch updates if you opt in), and improve and secure the Service.`,
  },
  {
    heading: "How We Store and Protect Information",
    body: `Your information is stored using Google Firebase and Google Cloud infrastructure. Passwords are hashed using industry-standard methods (PBKDF2-SHA256). While we take reasonable measures to protect your information, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.`,
  },
  {
    heading: "Third-Party Services",
    body: `We rely on third-party service providers to operate the Service, including Google Firebase and Google Cloud (hosting, database, and functions). We also retrieve publicly available NFL statistics and game data from ESPN; we do not share your personal information with ESPN or the NFL. These providers process data on our behalf subject to their own privacy policies.`,
  },
  {
    heading: "Sharing of Information",
    body: `We do not sell your personal information. We do not share your personal information with third parties for their own marketing purposes. We may share information with service providers who help us operate the Service, or when required by law.`,
  },
  {
    heading: "Your Rights",
    body: `Depending on where you live, you may have rights to access, correct, or delete your personal information, or to opt out of certain uses. To exercise these rights, contact us at [[CONTACT_EMAIL]]. Residents of California and certain other states may have additional rights under applicable privacy laws.`,
  },
  {
    heading: "Data Retention",
    body: `We retain your information for as long as your account is active or as needed to provide the Service. You may request deletion of your account and associated data by contacting us at [[CONTACT_EMAIL]].`,
  },
  {
    heading: "Children's Privacy",
    body: `The Service is intended for users 18 years of age and older. We do not knowingly collect personal information from anyone under 18. If we learn that we have collected such information, we will delete it.`,
  },
  {
    heading: "Changes to This Policy",
    body: `We may update this Privacy Policy from time to time. We will update the "Last Updated" date when we do. Continued use of the Service after changes become effective constitutes acceptance of the revised policy.`,
  },
  {
    heading: "Contact Us",
    body: `Questions about this Privacy Policy may be sent to [[CONTACT_EMAIL]] or [[MAILING_ADDRESS]].`,
  },
];

// --- Document renderer ---
function LegalDocument({ sections }) {
  return (
    <div>
      {sections.map((section, i) => (
        <div key={i} style={{ marginBottom: "24px" }}>
          <h3 style={{
            fontSize: "15px", fontWeight: 700, color: "#c9a227",
            margin: "0 0 8px", fontFamily: FONT_BODY,
            letterSpacing: "1px",
          }}>{i + 1}. {section.heading}</h3>
          <p style={{
            fontSize: "13px", color: "#a0a0b0", margin: 0,
            lineHeight: 1.7, fontFamily: FONT_BODY,
          }}>{section.body}</p>
        </div>
      ))}
    </div>
  );
}

// --- Main Legal Panel ---
export default function LegalPanel({ initialTab = "terms", onClose }) {
  const [tab, setTab] = useState(initialTab);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1100,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(8px)",
        }}
      />

      {/* Panel */}
      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: "520px", maxHeight: "88vh",
        margin: "20px",
        background: "rgba(12, 12, 22, 0.97)",
        border: "1px solid rgba(201, 162, 39, 0.2)",
        borderRadius: "16px",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>
        {/* Header with tabs */}
        <div style={{
          padding: "20px 24px 0",
          borderBottom: "1px solid rgba(201, 162, 39, 0.1)",
          flexShrink: 0,
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            marginBottom: "16px",
          }}>
            <h2 style={{
              fontSize: "16px", fontWeight: 700, color: "#c9a227",
              margin: 0, letterSpacing: "2px", textTransform: "uppercase",
              fontFamily: FONT_DISPLAY,
            }}>Legal</h2>
            <button
              onClick={onClose}
              style={{
                width: "30px", height: "30px",
                fontSize: "16px", color: "#5a5a6e",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "8px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >✕</button>
          </div>
          <div style={{ display: "flex", gap: "0" }}>
            {[
              { key: "terms", label: "Terms of Service" },
              { key: "privacy", label: "Privacy Policy" },
              { key: "community", label: "Community" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                style={{
                  padding: "10px 12px",
                  fontSize: "11px", fontWeight: 600, letterSpacing: "0.5px",
                  fontFamily: FONT_BODY,
                  color: tab === key ? "#c9a227" : "#5a5a6e",
                  background: "none",
                  border: "none",
                  borderBottom: `2px solid ${tab === key ? "#c9a227" : "transparent"}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                }}
              >{label}</button>
            ))}
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{
          padding: "24px",
          overflowY: "auto", flex: 1,
        }}>
          <p style={{
            fontSize: "11px", color: "#5a5a6e", margin: "0 0 20px",
            fontFamily: FONT_BODY,
          }}>Last Updated: [[LAST_UPDATED]]</p>
          {tab === "terms"
            ? <LegalDocument sections={TERMS_SECTIONS} />
            : tab === "privacy"
            ? <LegalDocument sections={PRIVACY_SECTIONS} />
            : <LegalDocument sections={COMMUNITY_SECTIONS} />}
        </div>
      </div>

      <style>{`
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(201, 162, 39, 0.2); border-radius: 4px; }
      `}</style>
    </div>
  );
}

// --- Inline text link (for signup consent line, footers) ---
export function LegalLink({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "none", border: "none", padding: 0,
        fontSize: "inherit", color: "#c9a227",
        fontFamily: "inherit", cursor: "pointer",
        textDecoration: "underline", textUnderlineOffset: "2px",
      }}
    >{label}</button>
  );
}
